import React, { useEffect, useState } from "react";
import axios from "axios";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import TeachersList from "./components/teachers";
import ExpelledStudents from "./components/expelled-students";
import NonAcademicStaffList from "./components/non-academic staffs"; // <-- Import the component
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const adminCards = [
  {
    key: "students",
    icon: <Icon name="Users" size={36} className="text-primary mb-2" />,
    title: "Students",
    desc: "View all students by class",
    route: null,
  },
  {
    key: "teachers",
    icon: <Icon name="UserCheck" size={36} className="text-accent mb-2" />,
    title: "Teachers",
    desc: "See all teachers in the school",
    route: null,
  },
  {
    key: "nonAcademic",
    icon: <Icon name="Briefcase" size={36} className="text-accent mb-2" />,
    title: "Non-Academic Staff",
    desc: "All non-academic staff",
    route: null,
  },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [nonAcademic, setNonAcademic] = useState([]);
  const [showSection, setShowSection] = useState(null); // 'students', 'teachers', 'nonAcademic'
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Fetch all classes on mount
  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await axios.get(`${API_BASE_URL}/api/user/class-list`);
      setClasses(res.data || []);
    } catch (err) {
      setErrorMsg("Failed to fetch classes.");
    }
    setLoading(false);
  };

  // Fetch all teachers
  const fetchTeachers = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await axios.get(`${API_BASE_URL}/api/add/teacher`);
      setTeachers(res.data || []);
    } catch (err) {
      setErrorMsg("Failed to fetch teachers.");
    }
    setLoading(false);
  };

  // Fetch all non-academic staff
  const fetchNonAcademic = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await axios.get(`${API_BASE_URL}/api/add/non-academic`);
      setNonAcademic(res.data || []);
    } catch (err) {
      setErrorMsg("Failed to fetch non-academic staff.");
    }
    setLoading(false);
  };

  // Fetch expelled/suspended students
 
  // Fetch students in a class
  const handleClassSelect = async (className) => {
    setSelectedClass(className);
    setLoading(true);
    setErrorMsg("");
    try {
      const studentsRes = await axios.get(
        `${API_BASE_URL}/api/user/class/${className}/all/students`
      );
      setStudents(studentsRes.data || []);
    } catch (err) {
      setErrorMsg("Failed to fetch class students.");
    }
    setLoading(false);
  };

  // Simple logout handler (clear adminData and reload or redirect)
  const handleLogout = () => {
    localStorage.removeItem("adminData");
    window.location.replace("/login-authentication");
  };

  // Filter users by search
  const filterUsers = (users) => {
    if (!search.trim()) return users;
    return users.filter(
      (user) =>
        user.name?.toLowerCase().includes(search.toLowerCase()) ||
        user.className?.toLowerCase().includes(search.toLowerCase())
    );
  };

  // Section handlers
  const handleSection = async (section) => {
    setShowSection(section);
    setSelectedClass(null);
    setSearch("");
    setErrorMsg("");
    if (section === "teachers") fetchTeachers();
    if (section === "nonAcademic") fetchNonAcademic();
   
    // students and verify don't need fetch here
  };

  // Only show cards if no section is selected
  const showCards = !showSection;

  return (
    <div className="min-h-screen bg-background relative">
      {/* Header: Admin Dashboard at the very top */}
      <div className="w-full flex flex-col items-center py-6 bg-card border-b border-border mb-2">
        <h1 className="text-3xl font-heading font-bold text-primary mb-2">
          Admin Dashboard
        </h1>
        <div className="w-full flex justify-end px-4">
          <Button variant="destructive" size="sm" onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </div>

      {/* Cards only show on dashboard, not when navigating to other sections */}
      {showCards && (
        <div className="w-full bg-white shadow-sm border-b border-border py-4 px-2 flex flex-wrap justify-center gap-8 sm:gap-12 md:gap-16">
          {adminCards.map((card) => (
            <div
              key={card.key}
              className="w-full sm:w-[220px] md:w-[260px] max-w-xs flex-shrink-0 bg-card border border-border rounded-xl p-4 flex flex-col items-center cursor-pointer hover:shadow-lg transition"
              onClick={() => {
                handleSection(card.key);
              }}
            >
              {card.icon}
              <h2 className="font-heading font-bold text-base mb-1">{card.title}</h2>
              <p className="text-muted-foreground text-xs text-center">{card.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 sm:p-8 transition-all duration-200">
        {/* Error Message */}
        {errorMsg && (
          <div className="mb-4 text-center text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {errorMsg}
          </div>
        )}

        {/* Main Section Content */}
        {!showSection && (
          <div className="mt-8">
            {/* Admin Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex flex-col items-center">
                <Icon name="Users" size={28} className="text-primary mb-2" />
                <div className="text-lg font-bold text-primary">
                  {
                    // Accurate total students count from all classes
                    classes.reduce((acc, cls) => acc + (cls.studentCount || 0), 0)
                  }
                </div>
                <div className="text-xs text-muted-foreground">Total Students</div>
              </div>
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 flex flex-col items-center">
                <Icon name="UserCheck" size={28} className="text-accent mb-2" />
                <div className="text-lg font-bold text-accent">
                  {
                    // Accurate total teachers count from teachers array
                    teachers.length
                  }
                </div>
                <div className="text-xs text-muted-foreground">Total Teachers</div>
              </div>
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 flex flex-col items-center">
                <Icon name="Briefcase" size={28} className="text-accent mb-2" />
                <div className="text-lg font-bold text-accent">
                  {
                    // Accurate total non-academic staff count from nonAcademic array
                    nonAcademic.length
                  }
                </div>
                <div className="text-xs text-muted-foreground">Non-Academic Staff</div>
              </div>
            </div>
            {/* Admin Notices */}
            <div className="bg-card border border-border rounded-xl p-6 mb-8 flex flex-col items-center">
              <h2 className="font-heading font-bold text-lg mb-4 text-primary">
                Admin Notices
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex flex-col items-center">
                  <Icon name="Database" size={24} className="text-primary mb-2" />
                  <div className="font-semibold text-primary mb-1">Backup Regularly</div>
                  <div className="text-xs text-muted-foreground text-center">
                    Remember to regularly back up the database to prevent data loss.
                  </div>
                </div>
                <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 flex flex-col items-center">
                  <Icon name="CheckCircle" size={24} className="text-accent mb-2" />
                  <div className="font-semibold text-accent mb-1">Review Results</div>
                  <div className="text-xs text-muted-foreground text-center">
                    Review and approve pending result verifications for accuracy.
                  </div>
                </div>
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 flex flex-col items-center">
                  <Icon name="Eye" size={24} className="text-warning mb-2" />
                  <div className="font-semibold text-warning mb-1">Monitor Activity</div>
                  <div className="text-xs text-muted-foreground text-center">
                    Monitor user activity for unusual or suspicious behavior.
                  </div>
                </div>
                <div className="bg-muted/10 border border-muted/20 rounded-lg p-4 flex flex-col items-center">
                  <Icon name="Edit3" size={24} className="text-muted-foreground mb-2" />
                  <div className="font-semibold text-muted-foreground mb-1">Update Records</div>
                  <div className="text-xs text-muted-foreground text-center">
                    Update staff and student records as needed to keep information current.
                  </div>
                </div>
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex flex-col items-center">
                  <Icon name="Bell" size={24} className="text-primary mb-2" />
                  <div className="font-semibold text-primary mb-1">Announcements</div>
                  <div className="text-xs text-muted-foreground text-center">
                    Check for new announcements to post for staff and students.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Students Section: Show classes, then students in class */}
        {showSection === "students" && !selectedClass && (
          <>
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => setShowSection(null)}
            >
              &larr; Back to Dashboard
            </Button>
            {/* Total number of students in the school */}
            <div className="mb-4 text-lg font-semibold text-primary">
              Total Students in School:{" "}
              {classes.reduce((acc, cls) => acc + (cls.studentCount || 0), 0)}
            </div>
            <h2 className="font-heading text-xl mb-4">All Classes</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {classes.map((cls) => (
                <div
                  key={cls.className}
                  className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:shadow-lg"
                  onClick={() => handleClassSelect(cls.className)}
                >
                  <div className="font-bold text-lg">{cls.className}</div>
                  <div className="text-muted-foreground text-sm">
                    Teacher: {cls.teacherName || "N/A"}
                  </div>
                  <div className="text-muted-foreground text-sm">
                    Students: {cls.studentCount || 0}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Students in selected class */}
        {showSection === "students" && selectedClass && (
          <>
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => setSelectedClass(null)}
            >
              &larr; Back to Classes
            </Button>
            <h2 className="font-heading text-xl mb-4">
              Students in {selectedClass}
            </h2>
            <div className="space-y-2">
              {filterUsers(students).map((student) => (
                <div
                  key={student._id}
                  className="flex items-center justify-between bg-muted/10 rounded-lg px-3 py-2"
                >
                  <span className="font-semibold">{student.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {student.className}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Teachers Section */}
        {showSection === "teachers" && (
          <>
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => setShowSection(null)}
            >
              &larr; Back to Dashboard
            </Button>
            <TeachersList />
          </>
        )}

        {/* Non-Academic Staff Section */}
        {showSection === "nonAcademic" && (
          <>
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => setShowSection(null)}
            >
              &larr; Back to Dashboard
            </Button>
            <NonAcademicStaffList /> {/* <-- Use the component here */}
          </>
        )}

        {/* Expelled/Suspended Students Section */}
        {showSection === "expelled" && (
          <>
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => setShowSection(null)}
            >
              &larr; Back to Dashboard
            </Button>
            <ExpelledStudents />
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;